import React, { useEffect, useRef, useState } from 'react';
import type { ConsultationMessage, ConsultationThread, User } from '../types';
import { getTranslation } from '../utils/translations';
import {
    buildConsultationThreadId,
    ensureConsultationThread,
    markConsultationThreadRead,
    sendConsultationMessage,
    subscribeConsultationMessages,
} from '../utils/consultationThreads';

interface ConsultationThreadPanelProps {
    clientId: string;
    consultantId: string;
    clientUid: string;
    householdId?: string;
    language?: User['language'];
}

const ConsultationThreadPanel: React.FC<ConsultationThreadPanelProps> = ({
    clientId,
    consultantId,
    clientUid,
    householdId,
    language = 'he',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language || 'en', key);
    const [thread, setThread] = useState<ConsultationThread | null>(null);
    const [messages, setMessages] = useState<ConsultationMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        ensureConsultationThread({
            clientId,
            consultantId,
            clientUid,
            consultantUid: consultantId,
            householdId,
        }).then((created) => {
            if (!cancelled) setThread(created);
        });
        return () => { cancelled = true; };
    }, [clientId, consultantId, clientUid, householdId]);

    useEffect(() => {
        const threadId = thread?.id || buildConsultationThreadId(clientId, consultantId);
        return subscribeConsultationMessages(threadId, setMessages);
    }, [thread?.id, clientId, consultantId]);

    useEffect(() => {
        const threadId = thread?.id || buildConsultationThreadId(clientId, consultantId);
        void markConsultationThreadRead(threadId);
    }, [thread?.id, clientId, consultantId]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        const threadId = thread?.id || buildConsultationThreadId(clientId, consultantId);
        if (!draft.trim()) return;
        setLoading(true);
        try {
            await sendConsultationMessage(threadId, 'consultant', draft, consultantId);
            setDraft('');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 text-xs font-black uppercase tracking-widest text-slate-500">
                {t('consultationBox')}
            </div>
            <div className="max-h-48 overflow-y-auto p-3 space-y-2 bg-white dark:bg-slate-900">
                {messages.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">{t('consultationEmpty')}</p>
                ) : (
                    messages.map((msg) => {
                        const isConsultant = msg.from === 'consultant';
                        return (
                            <div key={msg.id} className={`flex ${isConsultant ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] px-2.5 py-1.5 rounded-xl text-xs ${
                                    isConsultant
                                        ? 'bg-sky-600 text-white'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200'
                                }`}>
                                    {msg.text}
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={bottomRef} />
            </div>
            <form onSubmit={handleSend} className="p-2 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('consultationReplyPlaceholder')}
                    className="flex-1 p-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
                />
                <button type="submit" disabled={loading || !draft.trim()} className="px-3 py-2 bg-sky-600 text-white text-xs font-black rounded-lg disabled:opacity-50">
                    {t('send')}
                </button>
            </form>
        </div>
    );
};

export default ConsultationThreadPanel;
